/**
 * TypeSpecificSection — dispatcher for the type-specific slideout section(s)
 * rendered between OverviewSection and TimelineSection.
 *
 * Two mechanisms, checked in order:
 *  1. `components.typeSpecificSections` — an ordered list of declarative
 *     related-entity sections (rendered by `RelatedEntitySection`) and/or
 *     `custom` escape-hatch components. This is the config-driven path new
 *     sections use.
 *  2. Legacy `components.typeSpecific` — a single bespoke component. Retained
 *     for entity types not yet migrated to the list.
 *
 * Returns null for entities with neither — most types omit both.
 *
 * The `onRequestNoteCreation` callback is forwarded to `custom`/legacy
 * components (only meeting components consume it); declarative sections don't
 * take it.
 */

import type { CrmObject } from '@/types/crm';

import { getEntityTypeConfig } from '../../record/registry/entityTypeRegistry';
import { RelatedEntitySection } from './related-entity-section';

interface TypeSpecificSectionProps {
  object: CrmObject;
  /** Callback to request note creation in the NotesSection. */
  onRequestNoteCreation?: () => void;
}

export function TypeSpecificSection({
  object,
  onRequestNoteCreation,
}: TypeSpecificSectionProps) {
  const { components } = getEntityTypeConfig(object.entity_type_name);
  const sections = components?.typeSpecificSections;

  if (sections && sections.length > 0) {
    return (
      <>
        {sections.map(entry => {
          if (entry.kind === 'custom') {
            const Custom = entry.component;
            return (
              <Custom
                key={entry.key}
                object={object}
                onRequestNoteCreation={onRequestNoteCreation}
              />
            );
          }
          return (
            <RelatedEntitySection
              key={entry.config.key}
              config={entry.config}
              object={object}
            />
          );
        })}
      </>
    );
  }

  const TypeSpecific = components?.typeSpecific;
  if (!TypeSpecific) {
    return null;
  }
  return (
    <TypeSpecific
      object={object}
      onRequestNoteCreation={onRequestNoteCreation}
    />
  );
}
